// RichAds (richinfo.co) — TelegramAdsController: баннерная реклама.
//
// Скрипт грузим динамически (в index.html тег не нужен): так мы точно знаем,
// загрузился он или заблокирован (ERR_BLOCKED_BY_CLIENT = блокировщик рекламы).
//
// SDK сам создаёт свои виджеты на верхнем уровне body (fixed внизу) ещё во
// время initialize, поэтому работает «страж»: всё top-level чужое в body
// (кроме #root/#portal) вне сессии скрыто, во время сессии — захвачено
// в нашу карточку и удерживается там (SDK уносит DOM обратно — исправляем).

import { adLog } from './adDebug';

// RichAds привязывает appId к домену (traffic source в кабинете):
//   pleasurably-fortified-colt.cloudpub.ru → appId 9502 (виджет #411574)
//   test.mytadating.site                    → appId 9499 (виджет #411553)
// Сервер отдаёт виджеты только совпадающего домена, поэтому appId выбираем
// по фактическому hostname приложения.
const PUB_ID = '1027098';
const HOST_APP_MAP = [
  { match: /(^|\.)privately-cultured-mouflon\.cloudpub\.ru$/i, appId: '9503' },
  { match: /(^|\.)pleasurably-fortified-colt\.cloudpub\.ru$/i, appId: '9502' },
  { match: /(^|\.)mytadating\.site$/i, appId: '9499' }
];
const FALLBACK_APP_ID = '9502';

const resolveAppId = () => {
  const host = window.location.hostname;
  const hit = HOST_APP_MAP.find((h) => h.match.test(host));

  if (hit) return { appId: hit.appId, host, registered: true };

  // Туннельные домены cloudpub меняются при каждом перезапуске туннеля:
  // незарегистрированный в RichAds домен = сервер не отдаёт виджеты
  const unregisteredTunnel = /(^|\.)cloudpub\.ru$/i.test(host);
  return { appId: FALLBACK_APP_ID, host, registered: !unregisteredTunnel };
};

const SDK_URL = 'https://richinfo.co/richpartners/telegram/js/tg-ob.js';

const APP_IDS = new Set(['root', 'portal']);
const SERVICE_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'NOSCRIPT', 'META', 'TITLE']);
// Оверлей ошибок dev-сервера — не виджет SDK
const IGNORE_IDS = /^(webpack-dev-server-client-overlay|webpack-dev-server-client-overlay-div)$/i;

let initPromise = null;
let captureContainer = null; // во время сессии — DOM-элемент карточки
let adoptedWidgets = []; // захваченные виджеты: [{ el, originalParent, originalCss }]
let guardTimer = null;

const loadSdkScript = () =>
  new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SDK_URL}"]`);
    if (existing) {
      if (window.TelegramAdsController) return resolve();
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('script error')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.setAttribute('data-our', 'richads-sdk');
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Скрипт SDK не загрузился — домен заблокирован (адблок?) или сеть'));
    document.head.appendChild(script);
  });

const isForeignWidget = (el) =>
  el.nodeType === Node.ELEMENT_NODE &&
  !SERVICE_TAGS.has(el.tagName) &&
  !APP_IDS.has(el.id) &&
  !IGNORE_IDS.test(el.id || '') &&
  !el.hasAttribute('data-our') &&
  el.parentElement === document.body;

// Пустая обёртка SDK (без контента) успехом не считается — иначе карточка
// «показывает» невидимый контейнер, как это было с «widget_id not initialize»
const hasVisibleContent = (el) =>
  Boolean(el.children.length > 0 || (el.textContent && el.textContent.trim().length > 0));

const adopt = (el) => {
  el.style.setProperty('position', 'relative', 'important');
  el.style.setProperty('left', 'auto', 'important');
  el.style.setProperty('right', 'auto', 'important');
  el.style.setProperty('top', 'auto', 'important');
  el.style.setProperty('bottom', 'auto', 'important');
  el.style.setProperty('width', '100%', 'important');
  el.style.setProperty('max-width', '100%', 'important');
  el.style.setProperty('margin', '0 auto', 'important');
  el.style.setProperty('display', 'block', 'important');
  if (captureContainer && el.parentElement !== captureContainer) {
    captureContainer.appendChild(el);
  }
};

// Обёртка виджета для элемента, созданного на любой глубине:
// поднимаемся до верхнеуровневого ребёнка body, но не через наши корни
const wrapperFor = (node) => {
  let current = node;
  while (current && current !== document.body) {
    if (current.id && APP_IDS.has(current.id)) return null;
    if (current.hasAttribute?.('data-our')) return null;
    if (current.parentElement === document.body) return isForeignWidget(current) ? current : null;
    current = current.parentElement;
  }
  return null;
};

// Страж: следим за ЛЮБОЙ глубиной body (SDK может создать виджет вложенно),
// чужое — прячем вне сессии, в сессии забираем в карточку
const sweep = (nodes) => {
  const wrappers = new Set();

  if (nodes) {
    nodes.forEach((n) => {
      if (n.nodeType !== Node.ELEMENT_NODE) return;
      if (n.id && APP_IDS.has(n.id)) return;
      const w = wrapperFor(n);
      if (w) wrappers.add(w);
    });
  } else {
    [...document.body.children].forEach((el) => {
      if (isForeignWidget(el)) wrappers.add(el);
    });
  }

  wrappers.forEach((el) => {
    if (captureContainer) {
      if (!adoptedWidgets.some((w) => w.el === el)) {
        adoptedWidgets.push({ el, originalParent: el.parentElement, originalCss: el.getAttribute('style') || '' });
        adLog(`Страж: виджет SDK захвачен в карточку (top-level: ${el.tagName}#${el.id || '—'}${el.className ? '.' + String(el.className).slice(0, 40) : ''})`);
      }
      adopt(el);
    } else if (!adoptedWidgets.some((w) => w.el === el)) {
      el.style.setProperty('display', 'none', 'important');
    }
  });
};

const startGuard = () => {
  if (guardTimer) return;
  guardTimer = setInterval(() => sweep(), 1000);
  new MutationObserver((mutations) => {
    const added = [];
    mutations.forEach((m) => m.addedNodes.forEach((n) => added.push(n)));
    if (added.length > 0) sweep(added);
  }).observe(document.body, { childList: true, subtree: true });
  sweep();
};

// Перехват сетевых ответов SDK: сырой ответ сервера RichAds виден в логе
// (какие виджеты/статусы/гео он реально прислал)
const interceptSdkNetwork = () => {
  if (window.__richadsNetIntercepted) return;
  window.__richadsNetIntercepted = true;

  const isAdNetworkUrl = (url) =>
    /favorit\.work|richinfo\.co|richads\.com|7ool\.net|convers\.link/i.test(String(url));

  const logResponse = async (url, response) => {
    try {
      const clone = response.clone();
      const text = await clone.text();
      adLog(`NET ${String(url).replace(/^https?:\/\//, '').slice(0, 60)} → ${response.status}: ${text.slice(0, 300)}`);
    } catch (e) {
      adLog(`NET ${String(url).slice(0, 60)} → ${response.status}`);
    }
  };

  const originalFetch = window.fetch;
  window.fetch = (...args) => {
    const url = args[0]?.url || args[0];
    const promise = originalFetch.apply(window, args);
    if (isAdNetworkUrl(url)) promise.then((r) => logResponse(url, r)).catch(() => {});
    return promise;
  };

  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (isAdNetworkUrl(url)) {
      this.addEventListener('load', () => {
        adLog(
          `NET(XHR) ${String(url).replace(/^https?:\/\//, '').slice(0, 60)} → ${this.status}: ${String(this.responseText || '').slice(0, 300)}`
        );
      });
    }
    return origOpen.call(this, method, url, ...rest);
  };
};

export function initRichAds() {
  if (initPromise) return initPromise;

  initPromise = (async () => {
    adLog(`init: загружаю SDK (${SDK_URL})`);
    // Перехват сети — только для dev-сборки (отладка), прод не трогаем
    if (process.env.NODE_ENV !== 'production') {
      interceptSdkNetwork();
    }

    if (typeof window.TelegramAdsController !== 'function') {
      try {
        await loadSdkScript();
        adLog('init: скрипт SDK загружен');
      } catch (error) {
        adLog(`init: ${error.message}`);
        return false;
      }
    } else {
      adLog('init: SDK уже был загружен');
    }

    if (typeof window.TelegramAdsController !== 'function') {
      adLog('init: TelegramAdsController отсутствует после загрузки — скрипт заблокирован?');
      return false;
    }

    try {
      const { appId, host, registered } = resolveAppId();
      window.telegramAdsController = new window.TelegramAdsController();
      adLog(`init: домен ${host} → appId ${appId}${registered ? '' : ' (домен НЕ зарегистрирован в RichAds — виджетов не будет!)'}`);
      await window.telegramAdsController.initialize({ pubId: PUB_ID, appId });
      adLog('init: initialize OK, страж запущен');
      logWidgetConfig(window.telegramAdsController);
      startGuard();
      return true;
    } catch (error) {
      adLog(`init: ошибка initialize — ${error.message || error}`);
      startGuard();
      return false;
    }
  })();

  return initPromise;
}

// Диагностика конфигурации: какие форматы и widget id пришли с сервера RichAds.
// «widget_id not initialize» при показе означает, что нужного виджета тут нет.
function logWidgetConfig(controller) {
  try {
    const active = controller.activeWidgetTypesMap;
    const summary = {};

    if (active && typeof active === 'object') {
      Object.keys(active).forEach((k) => {
        const v = active[k];
        summary[k] = typeof v === 'object' && v !== null ? Object.keys(v).slice(0, 8) : v;
      });
    }

    const wm = controller.widgetManager;
    if (wm && typeof wm === 'object') {
      Object.keys(wm).forEach((k) => {
        if (/widget|ssp|banner|type/i.test(k)) {
          const v = wm[k];
          if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
            summary[`wm.${k}`] = v;
          }
        }
      });
    }

    adLog(`конфиг виджетов: ${JSON.stringify(summary)}`);
    if (!Object.keys(summary).some((k) => /widget|banner/i.test(k) && summary[k])) {
      adLog('конфиг пустой: у приложения нет ни одного виджета — создайте его в кабинете RichAds');
    }
  } catch (error) {
    adLog(`конфиг: не удалось прочитать — ${error.message}`);
  }
}

// Показ баннера в переданный контейнер карточки.
// resolve({ shown, dispose }): dispose — вызвать при закрытии карточки.
export function showRichAdsBanner(container, { timeoutMs = 10000 } = {}) {
  const controller = window.telegramAdsController;

  if (!controller || typeof controller.process !== 'function') {
    adLog('show: контроллера нет — SDK не инициализирован');
    return Promise.resolve({ shown: false, dispose: () => {} });
  }

  return new Promise((resolve) => {
    let settled = false;
    let timeout = null;
    let capturePoll = null;

    const dispose = () => {
      if (timeout) clearTimeout(timeout);
      if (capturePoll) clearInterval(capturePoll);
      if (captureContainer !== container) return;
      captureContainer = null;
      adoptedWidgets.forEach(({ el, originalParent, originalCss }) => {
        try {
          if (el.parentElement === container) {
            el.setAttribute('style', originalCss);
            originalParent.appendChild(el);
          }
        } catch (e) {
          adLog(`dispose: ${e.message}`);
        }
      });
      adoptedWidgets = [];
    };

    captureContainer = container;
    sweep();
    adLog('show: сессия открыта, показываю embedded-баннер');

    // Состояние SDK на момент показа: включённые типы и id виджета баннера
    try {
      const active = controller.activeWidgetTypesMap;
      adLog(`show: activeWidgetTypesMap = ${JSON.stringify(active)}`);
      const wm = controller.widgetManager;
      const state = {
        publisherInfo: controller.publisherInfo ?? wm?.publisherInfo ?? null,
        widgetTypes: wm?.widgetTypes ?? null
      };
      adLog(`show: состояние SDK = ${JSON.stringify(state)?.slice(0, 1500)}`);

      // Ключи SDK в localStorage: капы и ожидания хранятся там
      const sdkKeys = Object.keys(window.localStorage || {}).filter((k) =>
        /rich|widget|banner|push|cap|expect/i.test(k)
      );
      adLog(`show: ключи SDK в localStorage = ${JSON.stringify(sdkKeys.map((k) => `${k}=${String(localStorage.getItem(k)).slice(0, 60)}`))}`);

      if (typeof controller.getActiveWidgetByAdType === 'function') {
        const widget = controller.getActiveWidgetByAdType(wm?.typeEmbeddedBanner || 'EMBEDDED_BANNER');
        adLog(`show: getActiveWidgetByAdType(EMBEDDED_BANNER) = ${JSON.stringify(widget)?.slice(0, 300)}`);
      }
      if (wm?.getWidgetIdByType) {
        const id = wm.getWidgetIdByType(wm.typeEmbeddedBanner || 'EMBEDDED_BANNER');
        adLog(`show: getWidgetIdByType(EMBEDDED_BANNER) = ${JSON.stringify(id)}`);
      }
    } catch (e) {
      adLog(`show: диагностика состояния — ${e.message}`);
    }

    // Штатная точка входа — process(): сам готовит фид-данные и вызывает
    // обработчики включённых форматов. По фактическому серверному конфигу
    // (ACTIVE_WIDGET_TYPE) у приложения активны PUSH_STYLE и
    // INTERSTITIAL_MIXED — поэтому дёргаем их триггеры; страж поймает
    // отрисованный виджет в карточку в любом случае.
    const callSdkShow = () => {
      const candidates = [
        { name: 'process', fn: controller.process },
        { name: 'triggerInterstitialMixed', fn: controller.triggerInterstitialMixed },
        { name: 'handlePushStyle', fn: controller.handlePushStyle },
        { name: 'triggerBannerAds', fn: controller.triggerBannerAds }
      ];

      for (const { name, fn } of candidates) {
        if (typeof fn !== 'function') continue;
        try {
          const result = fn.call(controller);
          adLog(`show: ${name} вызван, вернул: ${JSON.stringify(result)?.slice(0, 200) || String(result)}`);
          if (result && typeof result.catch === 'function') {
            result
              .then((r) => adLog(`show: ${name} завершился: ${JSON.stringify(r)?.slice(0, 200) || 'ok'}`))
              .catch((error) => adLog(`show: ${name} отклонён — ${error?.message || error}`));
          }
        } catch (error) {
          adLog(`show: ${name} кинул ошибку — ${error.message}`);
        }
      }
    };

    callSdkShow();

    // Повторный снимок состояния: карта типов могла наполниться асинхронно
    setTimeout(() => {
      adLog(`show(+3с): activeWidgetTypesMap = ${JSON.stringify(controller.activeWidgetTypesMap)}; захвачено виджетов: ${adoptedWidgets.length}`);
    }, 3000);

    capturePoll = setInterval(() => {
      if (settled) return;
      const withContent = adoptedWidgets.find((w) => hasVisibleContent(w.el));
      if (withContent) {
        settled = true;
        clearInterval(capturePoll);
        clearTimeout(timeout);
        adLog('show: баннер показан в карточке');
        resolve({ shown: true, dispose });
      }
    }, 150);

    timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      const capturedEmpty = adoptedWidgets.length > 0;
      adLog(
        capturedEmpty
          ? 'show: контейнер SDK захвачен, но пуст (нет фида или виджет не настроен)'
          : `show: за ${timeoutMs}мс виджет не появился (нет фида/блокировка)`
      );
      resolve({ shown: false, dispose });
    }, timeoutMs);
  });
}
